// ─────────────────────────────────────────────────────────────────────────────
// FUENTE DE VERDAD: ec-bo/src/analytics/core.ts
// Mantener byte-idéntico con ec-portal/src/analytics/core.ts (EC-3811).
//
// Singleton por el que pasa TODO evento. Responsabilidades:
//   1. Sanear (el único camino hacia el adapter).
//   2. Adjuntar super properties sin repetirlas en cada call site.
//   3. Bufferizar hasta que el contexto de tenant esté resuelto.
//   4. Deduplicar vistas de pantalla.
//
// La app nunca habla con el proveedor: habla con esto.
// ─────────────────────────────────────────────────────────────────────────────
import type {
  AnalyticsAdapter,
  AnalyticsContext,
  IdentityTraits,
  EmitOptions,
  SafeProps,
  TrackOptions,
} from "./types";
import { sanitizeProps, type Redaction } from "./sanitize";
import { ALLOW_KEYS, type AnalyticsEventMap, type AnalyticsEventName } from "./events";

interface QueuedEvent {
  kind: "track" | "page";
  name: string;
  props: SafeProps;
  opts?: TrackOptions;
  ts: number;
}

/** Tope del buffer. Si se llena, se descartan los más viejos. */
const MAX_QUEUE = 50;

/**
 * Red de seguridad: si el contexto asíncrono nunca resuelve (ec-bo re-resuelve
 * el company_client_id vía loadOperatorCompanyClient en cada boot), se manda
 * igual en vez de perder la sesión entera.
 */
const CONTEXT_TIMEOUT_MS = 8_000;

type RedactionHook = (redaction: Redaction) => void;

let adapter: AnalyticsAdapter | null = null;
let context: AnalyticsContext | null = null;
let superProps: SafeProps = {};
let contextReady = false;
let queue: QueuedEvent[] = [];
let lastScreenKey = "";
/**
 * Pantalla y subvista actuales, que el core adjunta solo a TODO evento (EC-3811,
 * Fase 2).
 *
 * Hace falta porque `resolveScreen(pathname)` no alcanza: buena parte de la
 * navegación de ec-bo no está en la URL —los 5 paneles del PortalB2B se navegan
 * por índice + sessionStorage, las ~17 subvistas de Stock & Envíos por un string
 * en estado local—. Sin esto, un cambio de filtro en la solapa Batería se
 * reporta igual que en Alertas, y ninguna interacción se puede ubicar.
 *
 * Se llenan desde page(), que es el único lugar que ya sabe dónde está el
 * usuario. Así ningún call site tiene que pasar `screen`, que es justo el dato
 * que un componente hoja no tiene.
 */
let currentScreen = "";
let currentSubview: string | null = null;
let onRedaction: RedactionHook = () => undefined;
let timeoutId: ReturnType<typeof setTimeout> | null = null;

function report(redactions: Redaction[], event: string): void {
  redactions.forEach((redaction) => onRedaction({ ...redaction, event }));
}

/** Recalcula las super properties saneadas. Se llama sólo cuando el contexto cambia. */
function refreshSuperProps(): void {
  if (!context) {
    superProps = {};
    return;
  }
  const { props, redactions } = sanitizeProps(
    context as unknown as Record<string, unknown>,
    ALLOW_KEYS
  );
  report(redactions, "$super_properties");
  superProps = props;
  adapter?.register(superProps);
}

function emit(event: QueuedEvent): void {
  if (!adapter) return;

  const props: SafeProps = { ...superProps, ...event.props };
  // Los eventos bufferizados conservan el momento real en que ocurrieron, en vez
  // de aparecer todos juntos en el instante del flush.
  const opts: EmitOptions = { ...event.opts, timestamp: event.ts };

  if (event.kind === "page") adapter.page(event.name, props, opts);
  else adapter.track(event.name, props, opts);
}

function enqueueOrEmit(event: QueuedEvent): void {
  if (!adapter) return;
  if (contextReady) {
    emit(event);
    return;
  }
  if (queue.length >= MAX_QUEUE) queue.shift();
  queue.push(event);
}

function clearTimer(): void {
  if (timeoutId !== null) {
    clearTimeout(timeoutId);
    timeoutId = null;
  }
}

export const analytics = {
  /** Lo llama bootstrap.ts, una sola vez, al arrancar la app. */
  init(
    nextAdapter: AnalyticsAdapter,
    baseContext: AnalyticsContext,
    hooks?: { onRedaction?: RedactionHook }
  ): void {
    adapter = nextAdapter;
    context = { ...baseContext };
    onRedaction = hooks?.onRedaction ?? (() => undefined);
    contextReady = false;
    queue = [];
    lastScreenKey = "";
    currentScreen = "";
    currentSubview = null;

    nextAdapter.init();
    refreshSuperProps();

    clearTimer();
    timeoutId = setTimeout(() => analytics.markContextReady("timeout"), CONTEXT_TIMEOUT_MS);
  },

  /**
   * Merge parcial de super properties. Idempotente: si nada cambió no toca el
   * adapter, para no escribir en localStorage en cada render de un bridge.
   */
  setContext(partial: Partial<AnalyticsContext>): void {
    if (!context) return;

    const current = context as unknown as Record<string, unknown>;
    let changed = false;
    for (const [key, value] of Object.entries(partial)) {
      if (value === undefined) continue;
      if (current[key] !== value) {
        current[key] = value;
        changed = true;
      }
    }

    if (changed) refreshSuperProps();
  },

  /**
   * Libera el buffer. La llaman los bridges cuando el contexto asíncrono está
   * resuelto, o cuando no hay contexto que esperar (rutas públicas).
   */
  markContextReady(_reason: "resolved" | "anonymous" | "timeout"): void {
    if (contextReady) return;
    contextReady = true;
    clearTimer();

    const pending = queue;
    queue = [];
    pending.forEach(emit);
  },

  track<E extends AnalyticsEventName>(
    event: E,
    props: AnalyticsEventMap[E],
    opts?: TrackOptions
  ): void {
    // La ubicación va primero para que un evento pueda pisarla explícitamente.
    const located: Record<string, unknown> = {
      ...(currentScreen ? { screen: currentScreen } : {}),
      ...(currentSubview ? { subview: currentSubview } : {}),
      ...(props as unknown as Record<string, unknown>),
    };

    const sanitized = sanitizeProps(located, ALLOW_KEYS);
    report(sanitized.redactions, event);

    if (sanitized.dropEvent) {
      onRedaction({ key: "*", rule: "event_dropped", event });
      return;
    }

    enqueueOrEmit({ kind: "track", name: event, props: sanitized.props, opts, ts: Date.now() });
  },

  /**
   * Vista de pantalla. Dedup por (screen, subview) porque varias pantallas
   * reescriben el querystring con `replace: true` sin cambiar de vista.
   */
  page(
    screen: string,
    extra: { subview?: string | null; path_shape?: string } = {}
  ): void {
    const key = `${screen}|${extra.subview ?? ""}`;
    // Se recuerda ANTES del dedup: al volver a una subvista ya vista el evento
    // se deduplica, pero la ubicación sigue siendo la correcta para los
    // siguientes track().
    currentScreen = screen;
    currentSubview = extra.subview ?? null;

    if (key === lastScreenKey) return;
    lastScreenKey = key;

    const sanitized = sanitizeProps({ screen, ...extra }, ALLOW_KEYS);
    report(sanitized.redactions, "screen_viewed");

    enqueueOrEmit({ kind: "page", name: screen, props: sanitized.props, ts: Date.now() });
  },

  /** `distinctId` debe ser el UUID interno del usuario. `traits` es un tipo cerrado. */
  identify(distinctId: string, traits: IdentityTraits): void {
    if (!adapter || !distinctId) return;

    const sanitized = sanitizeProps(traits as unknown as Record<string, unknown>, ALLOW_KEYS);
    report(sanitized.redactions, "$identify");
    adapter.identify(distinctId, sanitized.props);
  },

  /** Logout. Limpia identidad, dedup, buffer y el contexto propio del usuario. */
  reset(): void {
    lastScreenKey = "";
    currentScreen = "";
    currentSubview = null;
    queue = [];
    contextReady = false;
    clearTimer();

    analytics.setContext({
      role: null,
      company_client_id: null,
      company_client_scope: null,
      is_ellie_care_general: null,
      is_impersonating: false,
    });

    adapter?.reset();
  },

  /** Sólo para tests. */
  __reset(): void {
    clearTimer();
    adapter = null;
    context = null;
    superProps = {};
    contextReady = false;
    queue = [];
    lastScreenKey = "";
    currentScreen = "";
    currentSubview = null;
    onRedaction = () => undefined;
  },
};
